import React, { useState, useEffect, useRef } from "react";
import "./premiumcalc.css"
import { getFirestore, doc, setDoc, onSnapshot, deleteDoc, addDoc, collection, getDoc } from "firebase/firestore";
import { getMetadata, ref, uploadBytes } from "firebase/storage";
import { authSolar, dbSolar, storageSolar } from "../../../setup/FirebaseSolar";
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, signInAnonymously } from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from "firebase/app";
import { useNavigate } from 'react-router-dom';

export default function PremiumCalc() {
    // const [file, setFile] = useState(null);  
    const navigate = useNavigate();
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState(null);
    const [user, setUser] = useState(null);
    const [includeEV, setIncludeEV] = useState(false);
    const [includeBattery, setIncludeBattery] = useState(false);

    const fileInputRef = useRef(null);
    const [uploadingCSV, setUploadingCSV] = useState(false);
    const [uploadMessage, setUploadMessage] = useState("");

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(authSolar, (currentUser) => {
            if (currentUser) {
                console.log("Auth ready:", currentUser.uid);
                setUser(currentUser);

                // Firestore listener
                const docRef = doc(dbSolar, "premium", currentUser.uid);
                const unsubscribeDoc = onSnapshot(docRef, (docSnap) => {
                    if (docSnap.exists()) {
                        setValue(docSnap.data().annual_costs ?? docSnap.data());
                    }
                });

                return () => unsubscribeDoc();
            } else {
                // If no user, sign in anonymously
                if (!authSolar.currentUser) {
                    signInAnonymously(authSolar).catch((error) => {
                        console.error("Anon login failed:", error);
                    });
                }
            }
        });

        return () => unsubscribeAuth();
    }, []);

    async function deleteSolarDoc(uid) {
        try {
            const docRef = doc(dbSolar, "premium", uid);
            await deleteDoc(docRef);
            console.log(`Document ${uid} deleted successfully.`);
        } catch (error) {
            console.error("Error deleting document:", error);
        }
    }

    const daysOfWeek = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

    const [formData, setFormData] = useState({

        include_EV: false,
        include_Battery: false,
        Client_existing_PV_capacity: "",
        Client_proposed_PV_capacity: "",
        PV_cost_per_kW: "",
        PV_capacity_factor: "",
        PV_discount_rate: "",
        PV_asset_life: "",
        Daytime_EV_charging: false,
        max_storage_EV: "",
        max_charge_rate_EV: "",
        trickle_charge_rate_EV: "",
        EV_charging_days: [],
        avoided_petrol_cost: "",
        max_storage_battery: "",
        min_storage_battery: "",
        max_charge_rate_battery: "",
        max_discharge_rate_battery: "",
        battery_cost_per_kWh: "",
        battery_discount_rate: "",
        battery_asset_life: "",
        tariff_plan: "A1",
    });

    useEffect(() => {
        if (!includeEV) {
            setFormData(prev => ({
            ...prev,
            Daytime_EV_charging: false,
            max_storage_EV: 0,
            max_charge_rate_EV: 0,
            trickle_charge_rate_EV: 0,
            avoided_petrol_cost: 0,
            EV_charging_days: [],
            }));
        }
    }, [includeEV]);

    useEffect(() => {
        if (!includeBattery) {
            setFormData(prev => ({
            ...prev,
            max_storage_battery: 0,
            min_storage_battery: 0,
            max_charge_rate_battery: 0,
            max_discharge_rate_battery: 0,
            battery_cost_per_kWh: 0,
            battery_discount_rate: 0,
            battery_asset_life: 0,
            }));
        }
    }, [includeBattery]);

    useEffect(() => {
    setFormData(prev => ({
        ...prev,
        include_EV: includeEV,
        }));
    }, [includeEV]);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            include_Battery: includeBattery,
        }));
    }, [includeBattery]);

    useEffect(() => {
        if (!user) return;

        const docRef = doc(dbSolar, "premium", user.uid);

        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                setValue(docSnap.data().annual_costs ?? docSnap.data());
            }
        });

        return () => unsubscribe(); // clean up listener on unmount
    }, [user]); // <-- depend on user instead of []

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === "checkbox" && name === "EV_charging_days") {
            setFormData(prev => ({
                ...prev,
                EV_charging_days: checked
                    ? [...(Array.isArray(prev.EV_charging_days) ? prev.EV_charging_days : []), value]
                    : prev.EV_charging_days.filter(day => day !== value)
            }));
        } else if (type === "checkbox") {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };
    const handleCSVSelected = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setUploadingCSV(true);
            setUploadMessage("Uploading CSV...");

            const user = authSolar.currentUser;
            if (!user) {
                alert("User not signed in");
                return;
            }

            const storageRef = ref(storageSolar, `premium/${user.uid}.csv`);
            const metadata = { customMetadata: { uploadedBy: user.uid } };

            await uploadBytes(storageRef, file, metadata);

            setUploadedFileName(file.name); // ✅ store file name
            setUploadMessage(" uploaded successfully!");
        } catch (err) {
            console.error(err);
            setUploadMessage("Upload failed");
            setUploadedFileName("");
        } finally {
            setUploadingCSV(false);
            e.target.value = ""; // allow re-selecting same file
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();

        const user = authSolar.currentUser;
        if (!user) {
            alert("User not signed in!");
            return;
        }

        try {
            // Ensure the CSV exists
            const storageRef = ref(storageSolar, `premium/${user.uid}.csv`);
            await getMetadata(storageRef);
            await deleteSolarDoc(user.uid)

            // Convert numeric fields to numbers, boolean Daytime_EV_charging to "Yes"/"No"
            const payload = Object.fromEntries(
                Object.entries(formData).map(([key, value]) => {
                    if (key === "Daytime_EV_charging") return [key, value ? "Yes" : "No"];
                    if (
                        [
                            "Client_existing_PV_capacity",
                            "Client_proposed_PV_capacity",
                            "PV_cost_per_kW",
                            "PV_capacity_factor",
                            "PV_discount_rate",
                            "PV_asset_life",
                            "max_storage_EV",
                            "max_charge_rate_EV",
                            "trickle_charge_rate_EV",
                            "avoided_petrol_cost",
                            "max_storage_battery",
                            "min_storage_battery",
                            "max_charge_rate_battery",
                            "max_discharge_rate_battery",
                            "battery_cost_per_kWh",
                            "battery_discount_rate",
                            "battery_asset_life"
                        ].includes(key)
                    ) {
                        return [key, value === "" ? 0 : Number(value)];
                    }
                    return [key, value];
                })
            );

            await setDoc(doc(dbSolar, "premium", user.uid), payload, { merge: true });
            setLoading(true);

            // Listen for updates with annual_costs
            const docRef = doc(dbSolar, "premium", user.uid);
            const unsubscribe = onSnapshot(docRef, (docSnap) => {
                if (docSnap.exists() && docSnap.data().annual_costs) {
                    setValue(docSnap.data().annual_costs);
                    setLoading(false);
                    unsubscribe(); // stop listening after we get the costs
                }
            });
        } catch (err) {
            if (err.code === "storage/object-not-found") {
                alert("No CSV file found in Storage. Please upload one first.");
            } else {
                console.error("Error saving data:", err);
                alert("Failed to save data.");
            }
        }
    };

          const openBillingPortal = async () => {
            const user = authSolar.currentUser


            const customerDocRef = doc(dbSolar, "customers", user.uid);
            const customerDoc = await getDoc(customerDocRef);
            const customerId = customerDoc.data().stripeId;
            const portalRef = await addDoc(
                collection(dbSolar, "customers", user.uid, "portal_sessions"),
                {
                    customerId,
                    returnUrl: `${window.location.origin}/Premium-Solar-Calculator`, // redirect after portal
                }
            );
            const unsubscribe = onSnapshot(portalRef, (snap) => {
                const data = snap.data();
                if (data?.url) {
                    window.location.assign(data.url); // redirect to Stripe portal
                    unsubscribe(); // stop listening
                }
                if (data?.error) {
                    console.error("Stripe Portal Error:", data.error);
                    unsubscribe();
                }
            });
        
          }

    
    return (
        <div className='emas__solarinputs'>
            <div className="emas__solarinputs-heading"><h1>EMAS Premium Home Energy Calculator</h1></div>
            <div className="emas__solarinputs-prem" style={{ textAlign: "center" }} ><button onClick={() => openBillingPortal()}>Manage subscription</button></div>
            
            <div className='emas__solarinputs-file'>
                <h2>Electricity Usage Input </h2>
                <label>Log in to your Synergy account. Navigate to where you can view your electricity usage. Download a CSV file of your half hourly usage data for a 12 month period. Click "Upload CSV" below to select the downloaded file. </label>
                <label>If you have purchased an EV throughout the year choose a year of data up to the purchase date, unless you have had the EV for the whole year, in which case use the latest year. </label>
                <h2>  </h2>
                <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleCSVSelected}
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploadingCSV}
                >
                    {uploadingCSV ? "Uploading..." : "Upload CSV"}
                </button>



            {uploadMessage && <p>{uploadedFileName}{uploadMessage}</p>}
            </div>

            <form className="emas__solarinputs-form" onSubmit={handleSubmit}>
                <section>
                <h2>Synergy Tariff Plan</h2>

                <label>
                    <input
                    type="radio"
                    name="tariff_plan"
                    value="A1"
                    checked={formData.tariff_plan === "A1"}
                    onChange={handleChange}
                    />
                    A1 Tariff
                </label>

                <label>
                    <input
                    type="radio"
                    name="tariff_plan"
                    value="MiddaySaver"
                    checked={formData.tariff_plan === "MiddaySaver"}
                    onChange={handleChange}
                    />
                    Midday Saver Tariff
                </label>

                <label>
                    <input
                    type="radio"
                    name="tariff_plan"
                    value="EVAddOn"
                    checked={formData.tariff_plan === "EVAddOn"}
                    onChange={handleChange}
                    />
                    EV Add On Tariff
                </label>
                </section>
                <section>
                    <h2>Calculator Scope</h2>
                    <label>
                        <input
                            type="checkbox"
                            name="includeEV"
                            checked={includeEV}
                            onChange={e => setIncludeEV(e.target.checked)}
                        />
                        Include EV
                    </label>
                    <label>
                        <input
                            type="checkbox"
                            name="includeBattery"
                            checked={includeBattery}
                            onChange={e => setIncludeBattery(e.target.checked)}
                        />
                        Include Battery
                    </label>
                </section>

       
                <section>
                    <h2>PV Settings</h2>
                    <label>Existing PV Capacity (kW) e.g. 0 or 3</label>
                    <input
                        type="number"
                        name="Client_existing_PV_capacity"
                        value={formData.Client_existing_PV_capacity}
                        onChange={handleChange}
                        required
                    />
                    <label>Proposed PV Capacity (kW) e.g. 6</label>
                    <input
                        type="number"
                        name="Client_proposed_PV_capacity"
                        value={formData.Client_proposed_PV_capacity}
                        onChange={handleChange}
                        required
                    />
                    <label>PV Capacity Factor e.g. 0.85 for obstructed or not north facing; 1 for north facing</label>
                    <input
                        type="number"
                        name="PV_capacity_factor"
                        value={formData.PV_capacity_factor}
                        onChange={handleChange}
                        required
                    />

                    <label>PV Cost ($/kW) - this is the post-rebate cost per kW, e.g. 1000</label>
                    <input
                        type="number"
                        name="PV_cost_per_kW"
                        value={formData.PV_cost_per_kW}
                        onChange={handleChange}
                        required
                    />
                    <label>PV Discount Rate (%) - this is the annual opportunity cost of investing in PV, e.g. enter 5 for 5%</label>
                    <input
                        type="number"
                        name="PV_discount_rate"
                        value={formData.PV_discount_rate}
                        onChange={handleChange}
                        required
                    />
                    <label>PV Asset Life (years) e.g. 20</label>
                    <input
                        type="number"
                        name="PV_asset_life"
                        value={formData.PV_asset_life}
                        onChange={handleChange}
                        required
                    />

                </section>
                {includeEV &&
                <section >
                    <h2>EV Settings</h2>
                    <label>
                        <input
                            type="checkbox"
                            name="Daytime_EV_charging"
                            checked={formData.Daytime_EV_charging}
                            onChange={handleChange}
                        />
                        Daytime EV Charging
                    </label>
                    <label>Max Storage EV (kWh) e.g. 60</label>
                    <input
                        type="number"
                        name="max_storage_EV"
                        value={formData.max_storage_EV}
                        onChange={handleChange}
                        required
                    />
                    <label>Max Charge Rate EV (kWh/30 mins) e.g. 3.5</label>
                    <input
                        type="number"
                        name="max_charge_rate_EV"
                        value={formData.max_charge_rate_EV}
                        onChange={handleChange}
                        required
                    />
                    <label>Trickle Charge Rate EV (kWh/30 mins) e.g. 1</label>
                    <input
                        type="number"
                        name="trickle_charge_rate_EV"
                        value={formData.trickle_charge_rate_EV}
                        onChange={handleChange}
                        required
                    />

                    <label>EV Charging Days</label>
                    <div>
                        {daysOfWeek.map(day => (
                            <label key={day}>
                                <input
                                    type="checkbox"
                                    name="EV_charging_days"
                                    value={day}
                                    checked={formData.EV_charging_days.includes(day)}
                                    onChange={handleChange}
                                />
                                {day}
                            </label>
                        ))}
                    </div>

                    <label>Avoided Petrol Cost ($/year) e.g. 2000</label>
                    <input
                        type="number"
                        name="avoided_petrol_cost"
                        value={formData.avoided_petrol_cost}
                        onChange={handleChange}
                        required
                    />
                </section>
                }

                {includeBattery && 
                <section>
                    <h2>Battery Settings</h2>
                    <label>Max Storage (kWh) e.g. 13.5</label>
                    <input
                        type="number"
                        name="max_storage_battery"
                        value={formData.max_storage_battery}
                        onChange={handleChange}
                        required
                    />
                    <label>Min Storage (kWh) e.g. 2.5</label>
                    <input
                        type="number"
                        name="min_storage_battery"
                        value={formData.min_storage_battery}
                        onChange={handleChange}
                        required
                    />
                    <label>Max Charge Rate (kWh/30 mins) e.g. 2.5</label>
                    <input
                        type="number"
                        name="max_charge_rate_battery"
                        value={formData.max_charge_rate_battery}
                        onChange={handleChange}
                        required
                    />
                    <label>Max Discharge Rate (kWh/30 mins) e.g. 2.5</label>
                    <input
                        type="number"
                        name="max_discharge_rate_battery"
                        value={formData.max_discharge_rate_battery}
                        onChange={handleChange}
                        required
                    />
                    <label>Battery Cost ($/kWh) - this is the post-rebate cost per kWh, e.g. 800</label>
                    <input
                        type="number"
                        name="battery_cost_per_kWh"
                        value={formData.battery_cost_per_kWh}
                        onChange={handleChange}
                        required
                    />
                    <label>Battery Discount Rate - this is the annual opportunity cost of investing in a battery, e.g. enter 5 for 5% </label>
                    <input
                        type="number"
                        name="battery_discount_rate"
                        value={formData.battery_discount_rate}
                        onChange={handleChange}
                        required
                    />
                    <label>Battery Asset Life (years) e.g. 20</label>
                    <input
                        type="number"
                        name="battery_asset_life"
                        value={formData.battery_asset_life}
                        onChange={handleChange}
                        required
                    />
                </section>
                }

                <button type="submit" disabled={loading}>Submit</button>
            </form>
            <h2> Annual Cost for the next 10 years </h2>
            <label>Includes annual payments to Synergy and the annualised cost of investing in PV and/or battery</label>
            <label>This version of the calculator incorporates our electricity price forecasts.</label>
            <label>A financially worthwile investment will lower the annual costs.</label>
            <label>  </label>
{loading ? (
    <p>Loading annual costs...</p>
) : value && value.Year ? (
    <table className="emas__solarinputs-annual-costs-table">
        <thead>
            <tr>
                <th>Year</th>
                <th>Status Quo</th>
                <th>Add PV</th>
                <th>Add PV + EV</th>
                <th>Add PV + EV + Battery</th>
            </tr>
        </thead>
        <tbody>
            {value.Year.slice(0, 10).map((year, i) => (
                <tr key={year}>
                    <td>{year}</td>
                    <td>{value.Status_quo[i]?.toFixed(0)}</td>
                    <td>{value.Add_PV[i]?.toFixed(0)}</td>
                    <td>{value.Add_PV_EV[i]?.toFixed(0)}</td>
                    <td>{value.Add_PV_EV_Battery[i]?.toFixed(0)}</td>
                </tr>
            ))}
        </tbody>
    </table>
) : (
    <p>No annual costs yet.</p>
)}

    <p style = {{marginTop: "1rem"}}>
     <div className="emas__solarinputs-prem" style={{ textAlign: "center" }} ><button onClick={() => navigate("/Privacy-Policy")}>Privacy Policy</button></div>
    <label>Copyright © Electricity Market Advisory Services Pty Ltd 2026. </label>
    
    <label>Electricity Market Advisory Services Pty Ltd (ACN 663 507 828) does not guarantee, and accepts no legal liability arising from or connected to, the accuracy, reliability, currency or completeness of any material contained on this website. Parties should seek appropriate independent professional advice prior to relying on or entering into any commitment based on material published here.</label>
    </p>
       
</div>
        
    );
}
